import { type MappedLocation, LocationType } from 'shared';

import { SearchMode } from '@/shared/config';
import { getTravelType } from '@/shared/lib';

import type { SubmitSearchFormParams } from '../analytics';
import { CABIN_CLASS_MAP } from './constants';

const getCityForAirport = (location: MappedLocation) => {
  if (location.type === LocationType.AIRPORT) {
    return {
      ...location.city,
      country: location.country,
    };
  }
  return location;
};

const getSearchMode = (routesCount: number, hasReturnDate: boolean) => {
  if (routesCount > 1) {
    return SearchMode.MULTI_TRIP;
  }
  return hasReturnDate ? SearchMode.ROUND_TRIP : SearchMode.ONE_WAY;
};

export const extractSearchFormData = (params: SubmitSearchFormParams) => {
  const { passengers, routes, cabinClass } = params;
  const firstRoute = routes.value[0];
  const lastRoute = routes.value[routes.value.length - 1];
  const legsCount = routes.value.length;
  const isMultiTrip = legsCount > 1;

  const mappedPassengers = Object.entries(passengers).reduce(
    (newObject, nextEntry) => {
      const [paxType, amount] = nextEntry;
      return { ...newObject, [`passenger_${paxType}`]: amount };
    },
    {},
  );

  const departure = getCityForAirport(firstRoute.dep);
  const arrival = isMultiTrip
    ? getCityForAirport(lastRoute.arr)
    : getCityForAirport(firstRoute.arr);
  const dateTo = isMultiTrip
    ? lastRoute?.dates[0] || null
    : firstRoute?.dates[1] || null;

  const travelType = routes.value.some(r => getTravelType(r) === 'I')
    ? 'I'
    : 'D';

  return {
    city_from: departure,
    city_to: arrival,
    date_from: firstRoute?.dates[0],
    date_to: dateTo,
    search_mode: getSearchMode(legsCount, Boolean(firstRoute?.dates[1])),
    legs_count: legsCount,
    cabin_class: CABIN_CLASS_MAP[cabinClass.value],
    travel_type: travelType,
    ...mappedPassengers,
  };
};

export type ExtractSearchFormDataParams = ReturnType<
  typeof extractSearchFormData
>;
